import { ChevronDown, X } from 'lucide-react'
import { useEffect, type ButtonHTMLAttributes, type ComponentProps, type ReactNode, type SelectHTMLAttributes } from 'react'
import { Link, type LinkProps } from 'react-router'
import { cn, initials } from '../lib/utils'

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success'
type Size = 'sm' | 'md' | 'lg'

const variants: Record<Variant, string> = {
  primary: 'bg-navy-900 text-white hover:bg-navy-800 disabled:bg-navy-200 disabled:text-navy-400',
  secondary: 'bg-navy-50 text-navy-900 border border-navy-100 hover:bg-navy-100 disabled:text-navy-300',
  outline: 'bg-white text-navy-900 border border-navy-200 hover:bg-navy-50 disabled:text-navy-300',
  ghost: 'text-navy-700 hover:bg-navy-50 disabled:text-navy-300',
  danger: 'bg-white text-red-700 border border-red-200 hover:bg-red-50',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-200',
}
const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-[15px] gap-2.5',
}

const buttonClass = (variant: Variant, size: Size, className?: string) =>
  cn(
    'inline-flex items-center justify-center rounded-md font-medium transition-colors disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 whitespace-nowrap',
    variants[variant],
    sizes[size],
    className,
  )

export function Button({ variant = 'primary', size = 'md', className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size }) {
  return <button type="button" className={buttonClass(variant, size, className)} {...props} />
}

export function ButtonLink({ variant = 'primary', size = 'md', className, ...props }: LinkProps & { variant?: Variant; size?: Size }) {
  return <Link className={buttonClass(variant, size, className)} {...props} />
}

type Tone = 'neutral' | 'blue' | 'green' | 'amber' | 'red' | 'gold' | 'silver' | 'bronze' | 'navy'
const tones: Record<Tone, string> = {
  neutral: 'bg-navy-50 text-navy-500 border-navy-100',
  blue: 'bg-brand-50 text-brand-600 border-brand-100',
  green: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  amber: 'bg-amber-50 text-amber-700 border-amber-100',
  red: 'bg-red-50 text-red-700 border-red-100',
  gold: 'bg-gold-100 text-gold-700 border-gold-100',
  silver: 'bg-silver-100 text-silver-700 border-silver-100',
  bronze: 'bg-bronze-100 text-bronze-700 border-bronze-100',
  navy: 'bg-navy-900 text-white border-navy-900',
}

export function Badge({ tone = 'neutral', className, children }: { tone?: Tone; className?: string; children: ReactNode }) {
  return <span className={cn('inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium whitespace-nowrap', tones[tone], className)}>{children}</span>
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('rounded-lg border border-navy-100 bg-white', className)}>{children}</div>
}

export function Avatar({ name, size = 'md', className }: { name: string; size?: 'sm' | 'md' | 'lg' | 'xl'; className?: string }) {
  const s = { sm: 'size-7 text-[10px]', md: 'size-9 text-xs', lg: 'size-12 text-sm', xl: 'size-20 text-2xl' }[size]
  return <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full bg-navy-100 font-semibold text-navy-800', s, className)}>{initials(name)}</span>
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-xs font-medium uppercase tracking-[0.3em] text-navy-500', className)}>{children}</p>
}

export interface TabItem<T extends string> {
  value: T
  label: ReactNode
}

export function Tabs<T extends string>({ items, value, onChange, className, right }: { items: TabItem<T>[]; value: T; onChange: (v: T) => void; className?: string; right?: ReactNode }) {
  return (
    <div className={cn('flex flex-col-reverse gap-3 border-b border-navy-100 sm:flex-row sm:items-end sm:justify-between sm:gap-4', className)}>
      <div role="tablist" className="-mb-px flex min-w-0 gap-1 overflow-x-auto">
        {items.map((it) => (
          <button
            key={it.value}
            role="tab"
            type="button"
            aria-selected={value === it.value}
            onClick={() => onChange(it.value)}
            className={cn(
              'whitespace-nowrap border-b-2 px-3 pb-3 pt-2 text-sm transition-colors',
              value === it.value ? 'border-navy-900 font-semibold text-navy-900' : 'border-transparent text-navy-500 hover:text-navy-800',
            )}
          >
            {it.label}
          </button>
        ))}
      </div>
      {right && <div className="sm:pb-2">{right}</div>}
    </div>
  )
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={cn('relative inline-flex', className)}>
      <select
        className="h-9 w-full appearance-none rounded-md border border-navy-200 bg-white pl-3 pr-9 text-sm text-navy-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        {...props}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-navy-500" />
    </div>
  )
}

const fieldBase =
  'w-full rounded-md border border-navy-200 bg-white px-3 text-sm text-navy-900 placeholder:text-navy-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-navy-50'

export function Input({ className, ...props }: ComponentProps<'input'>) {
  return <input className={cn(fieldBase, 'h-10', className)} {...props} />
}

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return <textarea className={cn(fieldBase, 'py-2 leading-relaxed', className)} {...props} />
}

export function Field({ label, hint, children, className }: { label: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cn('block', className)}>
      <span className="mb-1.5 block text-sm font-medium text-navy-800">{label}</span>
      {children}
      {hint && <span className="mt-1.5 block text-xs text-navy-400">{hint}</span>}
    </label>
  )
}

export function Toggle({ checked, onChange, label, description }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string }) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div>
        <p className="text-sm font-medium text-navy-900">{label}</p>
        {description && <p className="mt-0.5 text-sm text-navy-500">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={cn('relative mt-0.5 inline-flex h-6 w-11 shrink-0 rounded-full transition-colors', checked ? 'bg-brand-600' : 'bg-navy-200')}
      >
        <span className={cn('absolute top-0.5 size-5 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-5.5' : 'translate-x-0.5')} />
      </button>
    </div>
  )
}

export function Modal({ open, onClose, title, children, footer, size = 'md' }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; size?: 'md' | 'lg' }) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-950/50" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={title} className={cn('relative flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-xl bg-white shadow-2xl', size === 'lg' ? 'max-w-3xl' : 'max-w-md')}>
        <div className="flex items-center justify-between border-b border-navy-100 px-6 py-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="rounded p-1 text-navy-400 hover:bg-navy-50 hover:text-navy-800" aria-label="Close">
            <X className="size-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-3 border-t border-navy-100 px-6 py-4">{footer}</div>}
      </div>
    </div>
  )
}

export function BackLink({ to, children = 'Back to training' }: { to: string; children?: ReactNode }) {
  return (
    <Link to={to} className="no-print inline-flex items-center gap-2 text-sm text-navy-600 hover:text-navy-900">
      <span aria-hidden>←</span> {children}
    </Link>
  )
}

export function EmptyState({ icon, title, children, action }: { icon: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center rounded-lg border border-dashed border-navy-200 px-6 py-14 text-center">
      <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-navy-50 text-navy-500">{icon}</div>
      <h3 className="font-semibold">{title}</h3>
      {children && <div className="mt-1 max-w-md text-sm text-navy-500">{children}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div>
      <p className="text-sm text-navy-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-navy-400">{sub}</p>}
    </div>
  )
}

export function ErrorNote({ children, className }: { children: ReactNode; className?: string }) {
  return <p role="alert" className={cn('rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800', className)}>{children}</p>
}
